'use client';

import { useEffect, useState } from 'react';
import { IBaseFilterModel, IGalleryFilters } from '../lib/website-api/types';
import { IOption } from '../components/atoms/at-dropdown/at-dropdown.types';

// In memory caches - note react cache is preferred for SSR
let filterCache: IGalleryFilters | null = null;

const mapOptions = (options: IBaseFilterModel[]) => {
  return options.map((m) => {
    return { value: m.Id.toString(), label: m.Name } as IOption;
  });
};

// Fetch Gallery Filter data (cached)
async function fetchGalleryFilters(): Promise<IGalleryFilters> {
  if (filterCache) return filterCache;

  const res = await fetch('/api/gallery/filters');

  if (!res.ok) throw new Error(`Failed to fetch Image Gallery Filters`);

  const data = await res.json();
  filterCache = data;
  return data;
}

interface IFilterValues {
  planSeriesOptions: IOption[];
  roomOptions: IOption[];
  exteriorOptions: IOption[];
  locationOptions: IOption[];
}

interface UseGalleryDataResult {
  filterValues: IFilterValues | null;
  loading: boolean;
}

// Main hook
export const useGalleryData = (): UseGalleryDataResult => {
  const [filterValues, setFilterValues] = useState<IFilterValues | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await fetchGalleryFilters();

        const value: IFilterValues = {
          planSeriesOptions: mapOptions(data.PlanSeries),
          exteriorOptions: mapOptions(data.ExteriorStyles),
          roomOptions: mapOptions(data.Rooms),
          locationOptions: mapOptions(data.Locations),
        };

        setFilterValues(value);
      } catch (err) {
        console.error('Failed to fetch gallery filters:', err);
        setFilterValues(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return {
    filterValues,
    loading,
  };
};
